import { TransactionTitle, TransferCounterpartPreviewRes } from '@dealstack/shared/lib/generated';
import type { InseparableRedemptionDisclosure } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/CreateNewTransactionDrawer/inseparableRedemption.utils.ts';

// CAP-2035: a single-title transfer also writes a linked leg on the other rail, so both rails
// fragment at the same serial boundary. The leg stays hidden from every read surface (it is
// net-zero plumbing), but it is still a row against a holder the user never named, and the
// resolver can refuse outright (120253) for a reason the form gives no hint of. Disclose it before
// the write, the way the redemption path already does.
//
// Reuses InseparableRedemptionDisclosure: same modal, same shape, so the two nominee paths read
// alike. No serial ranges — a transfer's serials are chosen by the fold at write time, so there is
// nothing truthful to show until it is saved.

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const RAIL_KEYS = {
	[TransactionTitle.LEGAL_TITLE]: {
		self: 'transactions.interest.legal',
		counterpart: 'transactions.interest.beneficial',
	},
	[TransactionTitle.BENEFICIAL_INTEREST]: {
		self: 'transactions.interest.beneficial',
		counterpart: 'transactions.interest.legal',
	},
};

const isSingleRail = (
	title: TransactionTitle | null | undefined
): title is TransactionTitle.LEGAL_TITLE | TransactionTitle.BENEFICIAL_INTEREST =>
	title === TransactionTitle.LEGAL_TITLE || title === TransactionTitle.BENEFICIAL_INTEREST;

/**
 * Null when there is nothing to disclose: a Both-titled transfer, or a preview that resolved to no
 * counterpart leg. The caller submits straight through in that case.
 */
export const buildTransferCounterpartDisclosure = (
	preview: TransferCounterpartPreviewRes,
	title: TransactionTitle | null | undefined,
	securityName: string,
	sellerId: string | null | undefined,
	holderNameById: (id: string) => string,
	formatUnits: (units: number) => string,
	t: TranslateFn
): InseparableRedemptionDisclosure | null => {
	if (!preview.writesCounterpart || !preview.counterpartHolderId || !isSingleRail(title)) {
		return null;
	}

	const rails = RAIL_KEYS[title];
	const selfRail = t(rails.self).toLowerCase();
	const counterpartRail = t(rails.counterpart).toLowerCase();
	const holder = holderNameById(preview.counterpartHolderId);
	const units = preview.units == null ? '' : formatUnits(preview.units);

	// The seller being their own counterpart is the deal's FIRST divergence: the rails were
	// identical until now, so the untouched rail fragments against the seller. Saying "stays with
	// the seller" is clearer there than naming them as if they were a third party.
	const sellerIsCounterpart = !!sellerId && sellerId === preview.counterpartHolderId;

	return {
		title: t('transactions.transfer_counterpart.title'),
		intro: sellerIsCounterpart
			? t('transactions.transfer_counterpart.intro_seller', {
					selfRail,
					counterpartRail,
				})
			: t('transactions.transfer_counterpart.intro', {
					selfRail,
					counterpartRail,
				}),
		bullets: [
			t('transactions.transfer_counterpart.leg', {
				holder,
				units,
				security: securityName,
			}),
		],
	};
};
